import _ from 'lodash';
import { formValueSelector } from 'redux-form';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  DATE_FORMAT_TRANSACTION_HISTORY,
  DATE_FORMAT_FOR_SERVER,
} from 'Common/Constants';
import { getControlInfoData, getOnline } from 'State/selectors';
import { TransactionEnum } from 'Common/FieldEnum';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import { MealTypesEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { getCurrentSelectedMenu } from 'PointOfSale/modules/mealTypes/state/selector';
import { getQueue } from 'PointOfSale/modules/queue/state/selectors';
import { getStudentsForTeacher } from 'PointOfSale/modules/teacher/state/selectors';
import { SessionTypes } from 'PointOfSale/modules/common/constants';
import {
  getSelectedDate,
  getSelectedSiteDetails,
} from 'PointOfSale/modules/setDateAndSite/state/selectors';
import {
  getProfile,
  getActiveSession,
} from 'PointOfSale/modules/individualServing/state/selectors';
import {
  getTax,
  getCost,
  getChange,
  getBalance,
  getTaxRate,
  getSubtotal,
  getAmountPaid,
  getBoughtItems,
  hasBoughtItems,
  getPendingAccountBalance,
  getTotalReimbursableAmount,
} from 'PointOfSale/helpers/transactions';
import { isPreferenceDisabled } from 'Common/Security/SecurityHelpers';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';
import * as SecuritySelectors from 'Common/Security/state/selectors';
import { add, multiply } from 'Common/Helpers/currency';

const transactionFormSelector = formValueSelector(TRANSACTION_FORM_NAME);

export const getCurrentTransaction = state =>
  _.get(state, 'PointOfSaleState.common.currentTransaction');

export const getCurrentTransactionCheckNumber = state => {
  return transactionFormSelector(state, TransactionEnum.CheckNumber) || '';
};

export const getCurrentTransactionAmountPaid = state => {
  return transactionFormSelector(state, TransactionEnum.AmountPaid);
};

export const getCurrentTransactionCost = state => {
  const subTotal = getSubtotal(getBoughtItems(getCurrentTransaction(state)));
  return getCost(subTotal, getCurrentTransactionTax(state));
};

export const getCurrentTransactionSubtotal = state => {
  return getSubtotal(getBoughtItems(getCurrentTransaction(state)));
};

export const getCurrentTaxRate = state => {
  return getTaxRate(getProfile(state), getTaxRatesByStatus(state));
};

export const getCurrentTransactionTax = state => {
  const items = getBoughtItems(getCurrentTransaction(state));
  const taxRate = getCurrentTaxRate(state);
  let taxAmount = 0;
  items.forEach(item => {
    taxAmount = add(
      taxAmount,
      multiply(getTax(item.UnitPrice, taxRate), item.Qty)
    );
  });

  return taxAmount;
};

export const getCurrentTransactionChange = state => {
  return getChange(
    getAmountPaid(getCurrentTransaction(state)),
    getCurrentTransactionCost(state),
    getBalance(getProfile(state))
  );
};

export const getCurrentTransactionBoughtItems = state => {
  return getBoughtItems(getCurrentTransaction(state));
};

export const getCurrentTransactionPendingBalance = state => {
  return getPendingAccountBalance(
    getBalance(getProfile(state)),
    getCurrentTransactionCost(state)
  );
};

export const getCurrentTransactionBalance = state => {
  return getBalance(getProfile(state));
};

export const getCurrentTransactionTotalReimbursableAmount = state =>
  getTotalReimbursableAmount(getCurrentTransaction(state));

export const getServingSites = state =>
  _.get(state, 'PointOfSaleState.common.servingSites', []);

export const getViewCurrentStudentHistory = state =>
  _.get(state, 'PointOfSaleState.common.viewCurrentStudentHistory', false);

export const getServedItems = state =>
  _.get(state, 'PointOfSaleState.common.servedItems', []);

export const getServedItemsOrder = state =>
  _.get(state, 'PointOfSaleState.common.servedItemsOrder', []);

export const getNutritionCodes = state =>
  _.get(state, 'PointOfSaleState.common.nutritionCodes', []);

export const getItemCategories = state =>
  _.get(state, 'PointOfSaleState.common.itemCategories', []);

export const getFetching = state =>
  _.get(state, 'PointOfSaleState.common.fetching');

export const getWriteTransactionError = state =>
  _.get(state, 'PointOfSaleState.common.writeTransactionError');

export const getFetchError = state =>
  _.get(state, 'PointOfSaleState.common.fetchError');

export const getTaxRatesByStatus = state =>
  _.get(state, 'PointOfSaleState.common.taxRatesByStatus', []);

export const getShowAllergenDialog = state => {
  const isOnline = getOnline(state);
  if (!isOnline) {
    return true;
  }
  return _.get(getControlInfoData(state), 'AllergenMessage', true) === true;
};

export const getShowSpecialMessageDialog = state => {
  const isOnline = getOnline(state);
  if (!isOnline) {
    return true;
  }
  return (
    _.get(getControlInfoData(state), 'SpecialServingMessage', true) === true
  );
};

/**
 * Allow visiting students to be served via pinpad
 * @returns {Boolean}
 */
export const getServeVisitingStudentsViaPinPad = createSelector(
  SecuritySelectors.getPreferences,
  preferences =>
    !isPreferenceDisabled(
      PreferencesEnum.ServeVisitingStudentsViaPinPad,
      preferences
    )
);

export const hasStudentInQueue = (state, idSeed) => {
  const students = getQueue(state);
  return students.some(student => student.IdSeed === idSeed);
};

export const hasStudentInTeacher = (state, idSeed) => {
  const students = getStudentsForTeacher(state);
  return students.some(student => student.IdSeed === idSeed);
};

export const hasMultipleInQueue = (state, idSeed) => {
  const students = getQueue(state);
  return students.filter(student => student.IdSeed === idSeed).length > 1;
};

export const hasMultipleInTeacher = (state, idSeed) => {
  const students = getStudentsForTeacher(state);
  return students.filter(student => student.IdSeed === idSeed).length > 1;
};

export const hasStudentInOtherSessions = (state, idSeed) => {
  const activeSession = getActiveSession(state);

  if (activeSession === SessionTypes.Queue) {
    return hasStudentInTeacher(state, idSeed);
  }

  if (activeSession === SessionTypes.Teacher) {
    return hasStudentInQueue(state, idSeed);
  }
  return false;
};

export const getHasTeacherStudentsSelected = state => {
  const students = getStudentsForTeacher(state);
  return students.some(student => student.selected);
};

export const getHasBoughtItems = state => {
  return hasBoughtItems(getCurrentTransaction(state));
};

export const getHasPaidAmount = state => {
  const ap = getCurrentTransactionAmountPaid(state);
  return !_.isNil(ap) && ap !== 0;
};

export const shouldUpdateStudent = (state, idSeed) => {
  return (
    hasStudentInOtherSessions(state, idSeed) ||
    hasMultipleInTeacher(state, idSeed) ||
    hasMultipleInQueue(state, idSeed)
  );
};

/**
 * Retrieves request parameters used for POS
 */
export const getServingDetails = state => {
  const selectedSiteDetails = getSelectedSiteDetails(state);
  const currentSelectedMenu = getCurrentSelectedMenu(state);
  const selectedDate = getSelectedDate(state);
  const date = moment(selectedDate).isValid() ? moment(selectedDate) : moment();

  const PrimarySiteId = selectedSiteDetails.Id;
  const ServerId = SecuritySelectors.getId(state);
  const MealType = MealTypesEnum[currentSelectedMenu];
  const WorkingDT = date.format(DATE_FORMAT_TRANSACTION_HISTORY);
  const WorkingDate = date.format(DATE_FORMAT_FOR_SERVER);
  return {
    PrimarySiteId,
    ServerId,
    MealType,
    WorkingDT,
    WorkingDate,
  };
};

export const getSites = state =>
  _.get(state, 'PointOfSaleState.common.sites', []);

/**
 * Get primary site id, secondary site ids for for the current serving detail
 */
export const getSiteIds = state => {
  const { PrimarySiteId } = getServingDetails(state);
  const sites = getSites(state);
  const primarySite = sites.find(s => s.Id === PrimarySiteId);
  const siteIds = [PrimarySiteId];
  if (primarySite) {
    siteIds.push(...primarySite.SecondarySites.map(ss => ss.Id));
  }
  return siteIds;
};

export const getKeypadReadingLeft = state =>
  _.get(state, 'PointOfSaleState.common.keypadState.readingLeft');

export const getKeypadReadingRight = state =>
  _.get(state, 'PointOfSaleState.common.keypadState.readingRight');



// WEBPACK FOOTER //
// ./src/pages/pointofsale/state/selectors.js